import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig, DataServiceApiConstants, WweRcResolverService } from '@ddt-web/data-service';
import { StoreService } from '@ddt-web/store-service';
import { RiskProxySecurity, SecurityDetailsResponse, SecurityResolutionDetailsRequest, WealthWebApiService, WwePortfolioPosition, WwePosition, WweRiskProxyPortfolio, WweSecurity, WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import * as flatMap from 'lodash/flatMap';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as uniq from 'lodash/uniq';
import { combineLatest, of } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';


@Injectable()
export class PasRiskProxyDataLoaderService {

	public constructor(
		private storeService: StoreService,
		private wealthWebApiService: WealthWebApiService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private wweRcResolver: WweRcResolverService
	) { }

	public populateStoreForRiskProxies(filteredIds: string[], positionDataFromApiCall: WweSecurity[]): void {
		combineLatest([
			this.storeService.selectByIds<WwePortfolioPosition>(this.storeConfig.positionDetails, filteredIds).pipe(first()),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios)
		]).pipe(
			switchMap(([positionDetailsResult, associatedPortfoliosResult]) => {
				// The original securities within the portfolio, which were swapped out with proxied securities when calculating risk data
				const proxiedSecurities: WweSecurity[] = positionDataFromApiCall.filter(position => get(position, 'clients.bpasRiskProxy'));
				const storeProxies = flatMap(Object.values(positionDetailsResult.entities), portfolio => get(portfolio, 'children', []))
					.filter(position => get(position, 'clients.bpasRiskProxy'));
				proxiedSecurities.push(...storeProxies);

				if (!isEmpty(proxiedSecurities)) {
					// From the original scurities, we get aladdinIds of the securities used for the risk proxy calculations
					const proxies = proxiedSecurities.map(sec => get(sec, 'clients.bpasRiskProxy'));
					const aladdinIds = this.parseProxies(proxies);

					const body = this.buildSecurityDetailsParamsWithAladdinIds(aladdinIds);
					return this.wweRcResolver.getRcForUserType(DataServiceApiConstants.SECURITY_DETAILS).pipe(
						map((rc: string) => ({ body, rc })),
						switchMap((requestParams: WweSecurityDetailsParams) => this.wealthWebApiService.fetchSecurityDetails(requestParams)),
						take(1),
						map((response: SecurityDetailsResponse) => Array.from(response.securities).map(item => item[1])),
						map(riskProxySecurities => {
							const portfolios: WweRiskProxyPortfolio[] = [];
							Object.values(associatedPortfoliosResult.entities).forEach((wdsPortfolio: any) => {
								const proxyPortfolio: WweRiskProxyPortfolio = { id: wdsPortfolio.id, proxies: [] };
								// We have to loop through all securities from the wdsPortfolio, as that is the full list of securities for that portfolio. proxiedSecurities could be a partial list if this api call was part of an update portfolio flow
								const matchedProxiedPositions: WwePosition[] =
									get(wdsPortfolio, 'positions', []).map(wdsPosition => proxiedSecurities.find(security => security.aladdinId === wdsPosition.aladdinId)).filter(x => x);

								matchedProxiedPositions.forEach(position => {
									const bpasRiskProxyAttribute = get(position, 'clients.bpasRiskProxy');
									if (bpasRiskProxyAttribute) {
										const bpasRiskProxyIdentifier = this.parseProxy(bpasRiskProxyAttribute);
										const foundRiskProxySecurity = riskProxySecurities.find(riskProxySecurity => riskProxySecurity.aladdinId === bpasRiskProxyIdentifier);

										const proxySecurity: RiskProxySecurity = {
											...position,
											proxyIdentifier: get(foundRiskProxySecurity, 'isin') || bpasRiskProxyIdentifier,
											proxyDescription: get(foundRiskProxySecurity, 'description'),
											proxyError: !Boolean(foundRiskProxySecurity)
										};
										proxyPortfolio.proxies.push(proxySecurity);
									}
								});
								portfolios.push(proxyPortfolio);
							});
							return portfolios;
						})
					);
				} else {
					return of([]);
				}
			})
		).subscribe(portfolios => {
			if (portfolios.length) {
				this.storeService.upsertMany(this.storeConfig.riskProxies, portfolios);
			}
		});
	}

	private buildSecurityDetailsParamsWithAladdinIds(aladdinIds: string[]): SecurityResolutionDetailsRequest {
		return {
			securities: uniq(aladdinIds).map(aladdinId => ({
				id: aladdinId,
				identifiers: { aladdinId }
			})
			)
		} as SecurityResolutionDetailsRequest;
	}

	private parseProxies(proxies: string[]): string[] {
		return proxies.map(proxy => this.parseProxy(proxy));
	}

	// gets last values of pipe-delimited string e.g. 'A|B|C' -> 'C'
	private parseProxy(proxy: string): string {
		return proxy.replace(/.*?([^|]+)$/g, '$1');
	}
}
